import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/lib/shadcn/ui/dialog";
import { Button } from "@/lib/shadcn/ui/button";
import { Input } from "@/lib/shadcn/ui/input";
import { Label } from "@/lib/shadcn/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/shadcn/ui/select";
import { errorPopup } from "@/utils/alerts";
import { dateToString } from "@/utils/periods";
import { cn } from "@/lib/shadcn/utils";
import { ManagementFeeRatio, PayoutRatio } from "@/models/responses";
import { apiUrl } from "@/hooks/fetchOptions";

const MONTHS = [
    { value: 0, label: "January" },
    { value: 1, label: "February" },
    { value: 2, label: "March" },
    { value: 3, label: "April" },
    { value: 4, label: "May" },
    { value: 5, label: "June" },
    { value: 6, label: "July" },
    { value: 7, label: "August" },
    { value: 8, label: "September" },
    { value: 9, label: "October" },
    { value: 10, label: "November" },
    { value: 11, label: "December" },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

export interface EditApartmentRatioDialogProps {
    open: boolean;
    onClose: () => void;
    modalTitle: string;
    apartmentName: string;
    apartmentId: number;
    ratio: number;
    fromDate: Date;
    onSubmit: (ratio: number, month: number, year: number, existingRatio?: PayoutRatio | ManagementFeeRatio) => void;
}

export const EditRatioDialog: React.FC<EditApartmentRatioDialogProps> = ({
    open,
    onClose,
    modalTitle,
    apartmentName,
    apartmentId,
    ratio: initialRatio,
    fromDate,
    onSubmit,
}) => {
    const [ratio, setRatio] = useState<number>(initialRatio);
    const [isRatioValid, setIsRatioValid] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [allRatios, setAllRatios] = useState<(PayoutRatio | ManagementFeeRatio)[]>([]);

    useEffect(() => {
        setRatio(initialRatio);
        if (open && apartmentId) {
            fetchAllRatios();
        }
    }, [fromDate, initialRatio, open, apartmentId]);

    const fetchAllRatios = async () => {
        if (!apartmentId) return;

        try {
            const endpoint = modalTitle.toLowerCase().includes("payout") ? "payout-ratios" : "management-fee-ratios";
            const response = await fetch(`${apiUrl}/api/admin/apartments/${apartmentId}/${endpoint}`, {
                credentials: "include",
            });

            if (response.ok) {
                const data = await response.json();
                setAllRatios(data.payoutRatios || data.managementFeeRatios || []);
            }
        } catch (error) {
            console.error("Failed to fetch ratios:", error);
        }
    };

    const getExistingRatio = useCallback(() => {
        return allRatios.find((r) => {
            const ratioDate = new Date(r.fromDate);
            return ratioDate.getMonth() === selectedMonth && ratioDate.getFullYear() === selectedYear;
        });
    }, [allRatios, selectedMonth, selectedYear]);

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        try {
            const existingRatio = getExistingRatio();
            onSubmit(ratio, selectedMonth, selectedYear, existingRatio);
            handleClose();
        } catch (error) {
            errorPopup(`Failed to update: ${error}`);
        }
    };

    const handleClose = () => {
        setRatio(0);
        setIsRatioValid(true);
        onClose();
    };

    const validateAndSetRatio = useCallback((inputValue: string) => {
        const value = Number(inputValue) / 100;
        setRatio(value);
        setIsRatioValid(value >= 0 && value <= 1);
    }, []);

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{apartmentName}</DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Current Values Section */}
                    <div className="space-y-4">
                        <h3 className="text-center text-sm font-medium">Current {modalTitle}</h3>

                        <div className="space-y-2">
                            <Label>Value</Label>
                            <div className="flex">
                                <Input type="text" value={(initialRatio * 100).toFixed(0)} disabled className="rounded-r-none" />
                                <div className="flex items-center px-3 border border-l-0 rounded-r-md bg-gray-50">%</div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Last Updated</Label>
                            <Input type="text" value={dateToString(fromDate)} disabled />
                        </div>
                    </div>

                    <div className="h-px bg-gray-200" />

                    {/* New Values Section */}
                    <div className="space-y-4">
                        <h3 className="text-center text-sm font-medium">New {modalTitle}</h3>

                        <div className="space-y-2">
                            <Label>Value</Label>
                            <div className="flex">
                                <Input
                                    type="number"
                                    value={(ratio * 100).toFixed(0)}
                                    onChange={(e) => validateAndSetRatio(e.target.value)}
                                    className={cn("rounded-r-none", !isRatioValid && "border-red-500 focus-visible:ring-red-500")}
                                    required
                                />
                                <div className="flex items-center px-3 border border-l-0 rounded-r-md bg-gray-50">%</div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Effective From</Label>
                            <div className="grid grid-cols-2 gap-2">
                                <Select value={selectedMonth.toString()} onValueChange={(value) => setSelectedMonth(parseInt(value))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select month" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {MONTHS.map((month) => (
                                            <SelectItem key={month.value} value={month.value.toString()}>
                                                {month.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select year" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {YEARS.map((year) => (
                                            <SelectItem key={year} value={year.toString()}>
                                                {year}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <DialogFooter className="gap-2">
                        <Button type="button" variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="default">
                            Update
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
